import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cookie from 'react-cookie';
import classNames from 'classnames';
import isFunction from 'lodash.isfunction';
import { auth as authPropType } from 'scripts/constants/PropTypes';
import { routingSelector } from 'scripts/redux/selectors/routing';
import { authSelector } from 'scripts/redux/selectors/auth';
import { login } from 'scripts/redux/actions/user';
import { userSelector } from 'scripts/redux/selectors/user';
import {
  getDefaultAuthenticatedPath,
  isInvestor,
  isMember,
} from 'scripts/utilities/userAccountHelper';
import { getReferral } from 'scripts/utilities/referralHelper';
import { UserStatus } from 'scripts/constants/Cookie';
import Regex from 'scripts/utilities/regex';
import Authentication from 'scripts/constants/Authentication';
import Configuration from 'scripts/constants/Configuration';
import { getReferralCookie } from 'src/utils/referrals';
import { homeUrl } from 'src/utils/pageUrls';

const mapStateToProps = (state) => ({
  ...authSelector(state),
  ...userSelector(state),
  ...routingSelector(state),
});

@connect(mapStateToProps, {
  login,
})
export default class LoginForm extends Component {
  static propTypes = {
    login: PropTypes.func.isRequired,
    onLoginSuccess: PropTypes.func,
    className: PropTypes.string,
    auth: authPropType,
    doRedirect: PropTypes.bool,
    routing: PropTypes.object,
  };

  state = {
    email: {
      value: '',
      invalid: false,
    },
    password: {
      value: '',
      invalid: false,
    },
    javascriptReady: false,
  };

  componentDidMount() {
    this.setState({ javascriptReady: true }); // eslint-disable-line react/no-did-mount-set-state
  }

  componentDidUpdate() {
    const { auth } = this.props;

    const loggedIn =
      auth && auth.action === Authentication.LOGIN && auth.success;
    if (loggedIn) {
      this._onLoginSuccess();
    }
  }

  render() {
    const { className } = this.props;
    const { email, password } = this.state;
    return (
      <form
        id="login-form"
        className={classNames('login-form', className)}
        role="form"
        onSubmit={::this._onSubmit}
        data-test-reference="login-form"
      >
        <div className="form-group">
          <input
            type="text"
            className="login-form__field login-form__email"
            name="email"
            placeholder="Email"
            value={email.value}
            onChange={::this._onEmailChange}
            disabled={!this.state.javascriptReady}
            data-test-reference="login-form-email-input"
          />
          <div className="form-message__item error-email">
            {email.invalid && 'Please enter your valid email address'}
          </div>
        </div>
        <div className="form-group">
          <input
            type="password"
            className="login-form__field login-form__password"
            name="password"
            placeholder="Password"
            value={password.value}
            onChange={::this._onPasswordChange}
            disabled={!this.state.javascriptReady}
            data-test-reference="login-form-password-input"
          />
          <div className="form-message__item error-password">
            {password.invalid && 'Please enter your password'}
          </div>
        </div>
        <button
          type="submit"
          className="button orange-button right-arrow-button login-form__submit"
          disabled={!this.state.javascriptReady}
        >
          Login
        </button>
        <div className="forgot-link">
          <a href="/reset-password">Forgot your password?</a>
        </div>
      </form>
    );
  }

  _onEmailChange(e) {
    this.setState({ email: { value: e.target.value } });
  }

  _onPasswordChange(e) {
    this.setState({ password: { value: e.target.value } });
  }

  _validateEmail = () => {
    const email = this.state.email;
    email.invalid =
      !email.value.trim().match(Regex.email) || email.value.trim() === '';
    this.setState({ email: email });
  };

  _validatePassword = () => {
    const password = this.state.password;
    password.invalid = password.value === '';
    this.setState({ password: password });
  };

  _validateForm = () => {
    this._validateEmail();
    this._validatePassword();
  };

  _onSubmit(e) {
    e.preventDefault();
    this._validateForm();

    const { email, password } = this.state;
    if (email.invalid || password.invalid) {
      return;
    }

    const { login } = this.props;
    const { partnerReferralId } = getReferral();
    const { referralRecordId: customerReferralId } = getReferralCookie();
    login({
      email: email.value.trim(),
      password: password.value,
      customerReferralId,
      partnerReferralId,
    });
  }

  _setMembershipCookieTo = (userStatus) => {
    cookie.save(Configuration.COOKIES.MEMBERSHIP_STATUS, userStatus, {
      path: homeUrl(),
      maxAge: 1000 * 60 * 60 * 24 * 90,
    }); // eslint-disable-line no-magic-numbers
  };

  _onLoginSuccess = () => {
    const { auth, doRedirect, onLoginSuccess, routing } = this.props;
    const { isComponentUpdated } = this.state;

    if (!isComponentUpdated) {
      const loggedInUser = auth.user;
      if (isInvestor(loggedInUser)) {
        this._setMembershipCookieTo(UserStatus.Investor);
      } else if (isMember(loggedInUser)) {
        this._setMembershipCookieTo(UserStatus.Member);
      } else if (loggedInUser) {
        this._setMembershipCookieTo(UserStatus.Signup);
      }

      if (isFunction(onLoginSuccess)) {
        onLoginSuccess();
      }

      if (doRedirect) {
        if (!routing.locationBeforeTransitions.state) {
          window.location.href = getDefaultAuthenticatedPath(loggedInUser);
        } else {
          window.location.href =
            routing.locationBeforeTransitions.state.nextPathname;
        }
      }
      this.setState({
        isComponentUpdated: true,
      });
    }
  };
}
